import React from 'react'

import { bool, string, number, oneOf, shape } from 'prop-types'

import { useIs3dPrintModel, useRole } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import useUseCaseTranslate from 'Pods/LazyLoaded/UseCaseAreaSidebar/hooks/useUseCaseTranslate'

import UseCaseInfoItem from './Components/UseCaseInfoItem/UseCaseInfoItem'
import UseCaseUserInfo from './Components/UseCaseUserInfo/UseCaseUserInfo'
import {
  getTechnologyLabel, getMaterialLabel, getPrintTimeLabel, getPrintResultLabel,
} from '../../../../Utils/Data'

const DATE_OPTIONS = { day: 'numeric', month: 'short', year: 'numeric' }

// English keeps the existing en-GB "4 Nov 2026" format.
const formatAddedDate = (date, locale) =>
  new Date(date).toLocaleDateString(locale === 'en' ? 'en-GB' : locale, DATE_OPTIONS)

function UseCaseInfo({
  username,
  createdAt,
  description = null,
  technology = null,
  printerType = null,
  slicesDetails = null,
  avatarUrl = null,
  material = null,
  printSuccess = null,
  printTime = null,
  usedSupport = false,
  canDownloadSlicerFile = true,
  isOwner = false,
}) {
  const is3dPrintModel = useIs3dPrintModel()
  const role = useRole()
  const { t, locale } = useUseCaseTranslate()
  // "other" stays empty so UseCaseInfoItem shows it as not specified in every locale.
  const optionLabel = (value, getLabel) => (value && value !== 'other' ? getLabel(value, t) : null)
  const printerTypeLabel = printerType?.name || null
  const shouldShowPrintResult = printSuccess && !(role === 'seller' && isOwner)

  return (
    <div className="use-case-info">
      <div className="use-case-info__list">
        {is3dPrintModel && (
          <>
            <UseCaseInfoItem title={t('info.printerUsed')} value={printerTypeLabel} highlightValue />
            <UseCaseInfoItem
              title={t('info.material')}
              value={optionLabel(material, getMaterialLabel)}
              highlightValue
            />
            {shouldShowPrintResult && (
              <UseCaseInfoItem
                title={t('info.printResult')}
                value={optionLabel(printSuccess, getPrintResultLabel)}
                highlightValue
              />
            )}
            <UseCaseInfoItem
              title={t('info.slicerDetails')}
              value={slicesDetails}
              highlightValue
              showPendingLink
              canDownload={canDownloadSlicerFile}
            />
            <UseCaseInfoItem
              title={t('info.technology')}
              value={optionLabel(technology, getTechnologyLabel)}
              highlightValue
            />
            <UseCaseInfoItem
              title={t('info.printTime')}
              value={optionLabel(printTime, getPrintTimeLabel)}
              highlightValue
            />
            {usedSupport && <UseCaseInfoItem title={t('info.supportsUsed')} value={t('info.yes')} highlightValue />}
          </>
        )}
        <UseCaseUserInfo username={username} avatarUrl={avatarUrl} />
        <UseCaseInfoItem title={t('info.added')} value={formatAddedDate(createdAt, locale)} />
      </div>
      <div className="use-case-info__description">
        {description || t('info.noDescription')}
      </div>
    </div>
  )
}

UseCaseInfo.propTypes = {
  username: string.isRequired,
  createdAt: string.isRequired,
  description: string,
  technology: oneOf(['fdm', 'sla', 'sls', 'poly_jet', 'metal_printing', 'metal_jet', 'other', null]),
  printerType: shape({
    id: number,
    name: string,
    hidden: bool,
  }),
  slicesDetails: string,
  avatarUrl: string,
  material: string,
  printSuccess: string,
  printTime: string,
  usedSupport: bool,
  canDownloadSlicerFile: bool,
  isOwner: bool,
}

export default UseCaseInfo
